import React from 'react'
import { clsx } from 'clsx'
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon
} from '@heroicons/react/24/outline'

interface PaginationProps {
  currentPage: number
  totalPages: number
  pageSize?: number
  totalRecords?: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (pageSize: number) => void
  className?: string
  showFirstLast?: boolean
  maxVisiblePages?: number
}

export const Pagination = ({
  currentPage,
  totalPages,
  pageSize = 20,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  className,
  showFirstLast = true,
  maxVisiblePages = 5,
}: PaginationProps) => {
  // Calculate range for display (e.g., "1-50 sur 1200")
  const firstRecord = totalRecords ? (currentPage - 1) * pageSize + 1 : 0
  const lastRecord = totalRecords ? Math.min(currentPage * pageSize, totalRecords) : 0
  const getVisiblePages = () => {
    const pages: number[] = []
    let start = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const end = Math.min(totalPages, start + maxVisiblePages - 1)

    // Adjust start if we're near the end
    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1)
    }

    for (let i = start; i <= end; i++) {
      pages.push(i)
    }

    return pages
  }

  const visiblePages = getVisiblePages()

  const PaginationButton = ({ 
    onClick, 
    disabled, 
    children, 
    isActive = false,
    className: buttonClassName,
  }: {
    onClick: () => void
    disabled?: boolean
    children: React.ReactNode
    isActive?: boolean
    className?: string
  }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'inline-flex items-center justify-center px-3 py-2 text-sm font-normal rounded-md transition-colors',
        'border border-gray-300 dark:border-gray-600',
        isActive
          ? 'bg-sk-gold text-sk-black border-sk-gold dark:bg-sk-gold dark:border-sk-gold dark:text-sk-black'
          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700',
        disabled && 'opacity-50 cursor-not-allowed hover:bg-white dark:hover:bg-gray-800',
        buttonClassName
      )}
    >
      {children}
    </button>
  )

  return (
    <div className={clsx('flex items-center justify-between', className)}>
      {/* Page size selector */}
      {onPageSizeChange && (
        <div className="flex items-center">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="px-3 pr-8 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-sk-gold"
          >
            <option value="20">20 par page</option>
            <option value="50">50 par page</option>
            <option value="100">100 par page</option>
          </select>
        </div>
      )}

      {/* Navigation buttons - always show */}
      <nav className="flex items-center justify-center space-x-1">
        {/* First page */}
        {showFirstLast && totalPages > 1 && (
          <PaginationButton
            onClick={() => onPageChange(1)}
            disabled={currentPage === 1}
            className="mr-2"
          >
            <ChevronDoubleLeftIcon className="h-4 w-4" />
          </PaginationButton>
        )}

        {/* Previous page */}
        <PaginationButton
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </PaginationButton>

        {/* Page numbers */}
        {visiblePages[0] > 1 && (
          <>
            <PaginationButton onClick={() => onPageChange(1)}>
              1
            </PaginationButton>
            {visiblePages[0] > 2 && (
              <span className="px-2 py-2 text-gray-500">...</span>
            )}
          </>
        )}

        {visiblePages.map((page) => (
          <PaginationButton
            key={page}
            onClick={() => onPageChange(page)}
            isActive={page === currentPage}
          >
            {page}
          </PaginationButton>
        ))}

        {visiblePages[visiblePages.length - 1] < totalPages && (
          <>
            {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
              <span className="px-2 py-2 text-gray-500">...</span>
            )}
            <PaginationButton onClick={() => onPageChange(totalPages)}>
              {totalPages}
            </PaginationButton>
          </>
        )}

        {/* Next page */}
        <PaginationButton
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </PaginationButton>

        {/* Last page */}
        {showFirstLast && totalPages > 1 && (
          <PaginationButton
            onClick={() => onPageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="ml-2"
          >
            <ChevronDoubleRightIcon className="h-4 w-4" />
          </PaginationButton>
        )}
      </nav>

      {/* Records range display */}
      {totalRecords !== undefined && totalRecords > 0 && (
        <span className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
          {firstRecord}-{lastRecord} sur {totalRecords}
        </span>
      )}
    </div>
  )
}